import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageCircle, MapPin, Clock, Phone, Instagram, Mail, Shield, Award, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, whatsappMessage } from '../data/mock';

export const Footer = () => {
  const navigate = useNavigate();
  
  const openWhatsApp = () => {
    const message = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Background with motorcycle image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
          alt="Background"
          className="w-full h-full object-cover opacity-5"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/98 via-gray-800/95 to-gray-900/98" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* CTA Banner */}
        <div className="py-12 border-b border-gray-700/50">
          <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
            <div className="relative text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm mb-4">
                <Heart className="w-4 h-4" />
                <span className="text-sm font-semibold">Estamos aqui para cuidar de você</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Pronto para ter sua liberdade com economia?
              </h3>
              <p className="text-lg mb-6 opacity-95 max-w-2xl mx-auto">
                Converse com nossa equipe agora! Atendimento humanizado e os melhores preços da região te esperam.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/reserva')}
                  className="bg-white text-green-600 hover:bg-gray-100 font-bold px-8 py-6 rounded-xl text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Retirar Moto <span className="text-orange-500 font-extrabold">AGORA!</span>
                </Button>
                <Button 
                  onClick={() => window.open('https://www.instagram.com/desloca_?igsh=MWppeTNuenlxd3hibA==', '_blank')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-8 py-6 rounded-xl text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Seguir no Instagram
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-14 w-auto mb-4 filter brightness-0 invert"
              />
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                <Heart className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400">Cuidamos de Você</span>
              </div>
            </div>
            
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              A <strong className="text-white">Desloca</strong> é sua parceira de confiança para mobilidade na Baixada Santista. 
              Oferecemos motos zero km com os <strong className="text-green-400">melhores preços da região</strong> e 
              <strong className="text-white"> atendimento humanizado</strong> que faz toda a diferença.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { icon: Shield, text: 'Seguro Completo' },
                { icon: Heart, text: 'Suporte Humanizado' },
                { icon: Award, text: 'Melhor Preço' }
              ].map((badge, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm">
                  <badge.icon className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-gray-300">{badge.text}</span>
                </div>
              ))}
            </div>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <Button 
                onClick={openWhatsApp}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 rounded-xl"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
              
              <Button
                onClick={() => window.open('https://www.instagram.com/desloca_?igsh=MWppeTNuenlxd3hibA==', '_blank')}
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 rounded-xl"
              >
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-white flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full" />
              Links Rápidos
            </h3>
            <div className="space-y-3">
              {[
                { to: '/planos', label: 'Nossos Planos' },
                { to: '/', label: 'Home' },
                { to: '/reserva', label: 'Fazer Reserva' },
                { to: '/contato', label: 'Contato' }
              ].map((link, index) => (
                <Link 
                  key={index}
                  to={link.to} 
                  className="block text-gray-400 hover:text-green-400 transition-all duration-300 hover:translate-x-1 transform"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full opacity-0 hover:opacity-100 transition-opacity" />
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-white flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full" />
              Contato
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20 transition-colors">
                  <MapPin className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-gray-400">
                  <div className="font-semibold text-white text-sm">{companyInfo.location}</div>
                  <div className="text-sm">{companyInfo.coverage}</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20 transition-colors">
                  <Clock className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-gray-400 text-sm">
                  <div className="font-semibold text-white mb-1">Horário de Atendimento</div>
                  <div>Segunda a Sexta: 8h às 18h</div>
                  <div>Sábado: 8h às 16h</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20 transition-colors">
                  <Phone className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-gray-400">
                  <div className="font-semibold text-white text-sm">(13) 99620-8062</div>
                  <div className="text-sm">Suporte em tempo real</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700/50 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div className="text-center md:text-left">
              <p className="mb-1">
                &copy; 2025 <span className="text-white font-semibold">Desloca</span> - Locadora de Motos Premium. 
                Todos os direitos reservados.
              </p>
              <p className="text-xs" data-testid="footer-cnpj">
                CNPJ: {companyInfo.cnpj} • Atendemos toda a Baixada Santista com Excelência
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium">Site Seguro</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};